import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { formatDate } from '@app/functions/date-format';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TranslatableKey } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import { BIWEEKLY, TimeOffDateFormatDashes } from '@time-off-v3/meta/time-off-meta';
import { addWeeks, format, setISODay } from 'date-fns';
import moment from 'moment';

const BIWEEKLYFREQ = 2;

@Component({
    selector: 'app-bi-weekly-fields',
    templateUrl: './bi-weekly-fields.template.html',
    styleUrls: ['./bi-weekly-fields.style.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class BiWeeklyFieldsComponent implements OnDestroy {
    @Input() timeOffPolicy: TimeOffPolicy;
    @Input() containerClass = 'app-grid';
    @Input() fieldClass = 'columns-5 small-columns-12';
    @Input() firstDayOfPeriodLabel: TranslatableKey = 'time-off-v3.firstDayOfPeriod';

    @Output() requestPreviewParams: EventEmitter<string> = new EventEmitter();

    @ViewChild('startOfWeek') startOfWeek: MatSelect;

    weekday = '';
    listOfWeekdays = [];
    biWeeklyAnchorDates = [];

    constructor(private translateService: TranslateService) {
        this.listOfWeekdays = moment.weekdays().map((weekday) => weekday.toLowerCase());
    }

    getBiWeeklyAnchorDates(day: string): void {
        if (!day) {
            return;
        }

        const targetDay = moment().day(day).day();
        const today = moment().isoWeekday();

        // Check if we haven't gone past the target day in this week. If we have then use next weeks date for the calculations.
        if (today <= targetDay) {
            this.biWeeklyAnchorDates = this.generateAnchors(targetDay, 0);
            return;
        }

        this.biWeeklyAnchorDates = this.generateAnchors(targetDay, 1);
    }

    formatDate = (date: Date): string => formatDate(date, 'shortNoYear', this.translateService.currentLang);

    /*
     * The goal of this function is to get the dates for each anchor date. In a bi-weekly frequency that would 2 dates.
     * For e.g say we select Wednesday, April 29th as the date for accrual. This would be the one of the interval periods,
     * the other option we could have is the next Wednesday, May 6th. Since they are a week apart and re-occur bi-weekly
     * there will only ever be two cases. This weeks day or next weeks day. The rest of the calculation is using those dates
     * to produce string based on bi-weekly frequency.
     */
    generateAnchors(targetDay: number, weekOffset: number): { label: string; value: string }[] {
        const anchorDates = [];
        for (let i = weekOffset; i < weekOffset + BIWEEKLYFREQ; i++) {
            const firstDay = setISODay(addWeeks(new Date(), i), targetDay);
            const secondDay = setISODay(addWeeks(new Date(), i + BIWEEKLYFREQ), targetDay);

            anchorDates.push({
                label: `${this.formatDate(firstDay)}, ${this.formatDate(secondDay)}...`,
                value: format(firstDay, TimeOffDateFormatDashes),
            });
        }
        return anchorDates;
    }

    previewDataChanged(): void {
        this.requestPreviewParams.emit(BIWEEKLY);
    }

    ngOnInit(): void {
        this.getBiWeeklyAnchorDates(this.timeOffPolicy.startOfWeek);
    }

    ngOnDestroy(): void {
        this.timeOffPolicy.resetDateValues(this.timeOffPolicy);
    }
}
